import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../store";
import { fetchTrailUsage } from "../../../store/actions/PlanActions";
import { PiClockCountdownLight, PiWarningLight } from "react-icons/pi";
import Button from "../TailwindComponents/Button";
import { useHistory } from "react-router-dom";
import { UPGRADE_PATH } from "../../../config/entitlementKeys";

interface UsageEntry {
  used: number;
  limit: number | null;
}

interface TrailUsageData {
  is_trail: boolean;
  usage: Record<string, UsageEntry>;
}

const RESOURCE_LABELS: Record<string, string> = {
  matters: "Matters",
  tables: "Tables",
  data_sources: "Data Sources",
  reviews: "Reviews",
};

interface TrailUsageBannerProps {
  /** Only show usage for a specific resource (e.g. "matters" on the Matters page) */
  filterResource?: string;
  compact?: boolean;
}

const TrailUsageBanner: React.FC<TrailUsageBannerProps> = ({ filterResource, compact = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [data, setData] = useState<TrailUsageData | null>(null);

  useEffect(() => {
    let mounted = true;
    dispatch(fetchTrailUsage()).then((result: any) => {
      if (mounted && result) setData(result);
    });
    return () => { mounted = false; };
  }, [dispatch]);

  if (!data || !data.is_trail) return null;

  const entries = Object.entries(data.usage).filter(([key, val]) => {
    if (filterResource && key !== filterResource) return false;
    return val.limit !== null && val.limit !== undefined;
  });

  if (entries.length === 0) return null;

  const hasReachedLimit = entries.some(([, val]) => val.used >= (val.limit ?? Infinity));

  return (
    <div className={`rounded-lg border ${hasReachedLimit ? 'border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20' : 'border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20'} px-4 py-3`}>
      <div className="flex items-center gap-3 mb-2">
        {hasReachedLimit ? (
          <PiWarningLight className="h-5 w-5 text-danger-500 dark:text-danger-400 flex-shrink-0" />
        ) : (
          <PiClockCountdownLight className="h-5 w-5 text-warning-500 dark:text-warning-400 flex-shrink-0" />
        )}
        <span className={`text-sm font-medium ${hasReachedLimit ? 'text-danger-800 dark:text-danger-200' : 'text-warning-800 dark:text-warning-200'}`}>
          {hasReachedLimit ? t('Trial limit reached') : t('Trial usage')}
        </span>
        <div className="flex-1" />
        <Button
          variant="tertiary"
          size="small"
          onClick={() => history.push(UPGRADE_PATH)}
        >
          {t('Upgrade')}
        </Button>
      </div>

      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-4'} gap-3`}>
        {entries.map(([key, val]) => {
          const pct = val.limit ? Math.min(100, Math.round((val.used / val.limit) * 100)) : 0;
          const atLimit = val.used >= (val.limit ?? Infinity);
          const barColor = atLimit ? 'bg-danger-500' : pct >= 80 ? 'bg-warning-500' : 'bg-success-500';

          return (
            <div key={key} className="min-w-0">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-fg-body truncate">
                  {t(RESOURCE_LABELS[key] || key)}
                </span>
                <span className={`font-medium ${atLimit ? 'text-danger-600 dark:text-danger-400' : 'text-fg-muted'}`}>
                  {val.used}/{val.limit}
                </span>
              </div>
              <div className="h-1.5 w-full bg-surface-sunken rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrailUsageBanner;
